import React, { useMemo } from "react"

import { IIndexedValidationError } from "@common-shared/types/general.types.ts"

import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"
import { SpinnerElement } from "@front-shared/elements/spinner.element.tsx"

import { ScreenScrollableTableElement } from "../../../elements/screenScrollableTable.element"
import { NetworkStatusHelper } from "../../../helpers/networkStatus.helper"
import type { NetworkStatusRuleRow } from "../../../types/networkStatus.types"
import { RulesTableHeaderComponent } from "./rulesTableHeader.component"
import { RulesTableRowComponent } from "./rulesTableRow.component"

export const RulesTableComponent: React.FunctionComponent<
    IRulesTableComponentProps
> = props => {
    const errorByIndex = useMemo(() => {
        if (!props.errors?.length) {
            return new Map<number, { error: string; field?: string }>()
        }

        const mapped = new Map<number, { error: string; field?: string }>()

        for (const e of props.errors) {
            mapped.set(e.index, {
                error: e.error,
                ...(e.field ? { field: e.field } : {}),
            })
        }

        return mapped
    }, [props.errors])

    return (
        <div className={`relative ${props.className}`}>
            <ScreenScrollableTableElement
                tableRef={props.scrollableElementRef}
                headers={
                    <RulesTableHeaderComponent
                        showIdColumn={props.showIdColumn ?? true}
                    />
                }
            >
                {!props.errors &&
                (props.missingDataMessage || props.isListLoading) ? (
                    <>
                        <div className="fixed w-screen text-center mt-[32px]">
                            {props.missingDataMessage ? (
                                <PrimaryText typography="h6" centered>
                                    {props.missingDataMessage}
                                </PrimaryText>
                            ) : (
                                <SpinnerElement />
                            )}
                        </div>

                        {/*space holder since above is fixed to move with scroll*/}
                        <div className="mb-[96px]" />
                    </>
                ) : (
                    props.rules.map((rule, index) => {
                        const rowIndex =
                            "index" in rule && typeof rule.index === "number"
                                ? rule.index
                                : index

                        const err = props.errors
                            ? errorByIndex.get(rowIndex)
                            : undefined

                        return (
                            <RulesTableRowComponent
                                key={NetworkStatusHelper.getRuleRowKey(
                                    rule,
                                    index,
                                )}
                                rule={rule}
                                isLast={index === props.rules.length - 1}
                                rowError={err?.error}
                                errorField={err?.field}
                                showIdColumn={props.showIdColumn ?? true}
                            />
                        )
                    })
                )}
            </ScreenScrollableTableElement>
        </div>
    )
}

interface IRulesTableComponentProps {
    className?: string
    scrollableElementRef?: React.RefObject<HTMLDivElement>
    rules: NetworkStatusRuleRow[]
    isListLoading?: boolean
    missingDataMessage?: string
    // When provided, each row gets rowError/errorField from errors by rule index (review flow).
    errors?: IIndexedValidationError[]
    // Show ID column (default true). Set false for review-new-rules flow.
    showIdColumn?: boolean
}
